/* BP-003 — Repair Estimate tab.

   Layout:
     • Header row  — estimate metadata (name · sqft · target ARV) + actions
     • KPI hero    — Grand Total Rehab + contingency + $/sqft + % of ARV
     • Line items  — room · category · description · qty · unit · unit cost · line total
     • By category — donut-of-costs summary rollup
     • AI panel    — attached AI recommendation (opt-in)

   Publishes the current estimate → property.published_repair_estimate →
   consumed by BP-002 Wholesale, BP-005 Deal Score, BP-006 AI, BP-007 Fix & Flip.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Hammer, Loader2, Sparkles, Download, RotateCcw, Save,
  Plus, Trash2, Upload,
} from "lucide-react";
import { RepairBreakdownDonut } from "@/components/v3/PropertyCharts";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";
const INP_SM = "w-full bg-white border border-slate-200 rounded-md px-1.5 py-1 text-[12px] text-slate-900 focus:outline-none focus:border-amber-400";

const EMPTY_ITEM = () => ({
  room: "kitchen", category: "kitchen", description: "",
  qty: "1", unit: "each", unit_cost: "0", notes: "",
});

export default function RepairEstimateTab({ property }) {
  const [enums, setEnums] = useState({ rooms: [], categories: [], units: [] });
  const [templates, setTemplates] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [current, setCurrent] = useState(null);   // full estimate doc
  const [draft, setDraft] = useState({
    name: "Rehab v1",
    property_sqft: property.sqft || "",
    property_arv: property.published_arv || property.estimated_arv || "",
    items: [EMPTY_ITEM()],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [enumsRes, tplRes, estRes] = await Promise.all([
        api.get("/v3/repairs/enums"),
        api.get("/v3/repairs/templates"),
        api.get(`/v3/properties/${property.id}/repairs`),
      ]);
      setEnums(enumsRes.data);
      setTemplates(tplRes.data.items || []);
      setEstimates(estRes.data.items || []);
      const latest = estRes.data.items?.[0];
      if (latest) {
        setCurrent(latest);
        setDraft({
          name: latest.name,
          property_sqft: latest.property_sqft || "",
          property_arv: latest.property_arv || "",
          items: latest.items?.length ? latest.items : [EMPTY_ITEM()],
        });
      }
    } catch (err) { toast.error("Failed to load Repair Intelligence"); }
    finally { setLoading(false); }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => current?.totals || null, [current]);

  const updateItem = (i, field, value) => {
    setDraft((d) => {
      const items = [...d.items];
      items[i] = { ...items[i], [field]: value };
      // Auto-align category to room's default the first time user picks a room
      if (field === "room" && items[i].category === "kitchen" && value !== "kitchen") {
        items[i].category = ROOM_TO_CATEGORY[value] || items[i].category;
      }
      return { ...d, items };
    });
  };
  const addItem = () => setDraft((d) => ({ ...d, items: [...d.items, EMPTY_ITEM()] }));
  const removeItem = (i) => setDraft((d) => {
    const items = d.items.filter((_, idx) => idx !== i);
    return { ...d, items: items.length ? items : [EMPTY_ITEM()] };
  });
  const applyTemplate = (i, tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    setDraft((d) => {
      const items = [...d.items];
      items[i] = {
        ...items[i], category: t.category, description: t.description,
        unit: t.default_unit, qty: t.default_qty, unit_cost: t.default_unit_cost,
      };
      return { ...d, items };
    });
  };

  const save = useCallback(async () => {
    if (draft.items.length === 0 || draft.items.every((it) => !it.description)) {
      toast.error("Add at least one line item.");
      return;
    }
    setSaving(true);
    try {
      const cleanItems = draft.items
        .filter((it) => it.description)
        .map((it) => ({
          room: it.room, category: it.category, description: it.description,
          qty: it.qty || "0", unit: it.unit, unit_cost: it.unit_cost || "0",
          notes: it.notes || null,
        }));
      const payload = {
        name: draft.name || "Rehab v1",
        property_sqft: draft.property_sqft ? Number(draft.property_sqft) : null,
        property_arv: draft.property_arv || null,
        items: cleanItems,
      };
      let data;
      if (current?.id) {
        const res = await api.patch(`/v3/repairs/${current.id}`, payload);
        data = res.data;
        toast.success(`Estimate v${data.version} updated`);
      } else {
        const res = await api.post(`/v3/properties/${property.id}/repairs`, payload);
        data = res.data;
        toast.success(`Estimate created (v${data.version})`);
      }
      setCurrent(data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }, [draft, current, property.id, load]);

  const publish = useCallback(async () => {
    if (!current?.id) { toast.error("Save the estimate first."); return; }
    try {
      const { data } = await api.post(`/v3/repairs/${current.id}/publish`);
      setCurrent(data);
      toast.success("Published to every strategy engine");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    }
  }, [current, load]);

  const runAi = useCallback(async () => {
    if (!current?.id) { toast.error("Save the estimate first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/repairs/${current.id}/ai-analysis`);
      setCurrent(data);
      toast.success("AI analysis ready");
    } catch (err) { toast.error("AI analysis failed"); }
    finally { setAiLoading(false); }
  }, [current]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/repairs/${current.id}/export/pdf`, "_blank");
  }, [current]);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="repair-loading">
      <Loader2 size={14} className="animate-spin"/> Loading repair estimate…
    </div>
  );

  const status = current?.status || "draft";
  return (
    <div className="space-y-4" data-testid="tab-panel-repair_estimate">
      {/* Header + status */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
              <Hammer size={12}/> Repair Estimate
              {current && (
                <span className={`ml-2 text-[9px] font-semibold px-2 py-0.5 rounded-full border ${status === "published"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-slate-50 text-slate-600"}`}
                      data-testid="repair-status-pill">
                  {status.toUpperCase()}{current.version ? ` · v${current.version}` : ""}
                </span>
              )}
            </div>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              data-testid="repair-name"
              className="mt-1 w-full max-w-md text-lg font-semibold text-slate-900 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-amber-400 focus:outline-none pb-0.5"
              placeholder="Estimate name"/>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <label className="block">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Sqft (for $/sqft)</div>
                <input type="number" value={draft.property_sqft}
                       onChange={(e) => setDraft({ ...draft, property_sqft: e.target.value })}
                       data-testid="repair-sqft" className={INP_SM}/>
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Target ARV (for % of ARV)</div>
                <input type="number" value={draft.property_arv}
                       onChange={(e) => setDraft({ ...draft, property_arv: e.target.value })}
                       data-testid="repair-arv" className={INP_SM}/>
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 self-start">
            <button onClick={save} disabled={saving}
                    data-testid="repair-save"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>}
              {current?.id ? "Update" : "Save Draft"}
            </button>
            <button onClick={publish} disabled={!current?.id || status === "published"}
                    data-testid="repair-publish"
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-40">
              <Upload size={12}/> Publish
            </button>
            <button onClick={runAi} disabled={aiLoading || !current?.id}
                    data-testid="repair-ai"
                    className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
              {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              AI Analysis
            </button>
            <button onClick={exportPdf} disabled={!current?.id}
                    data-testid="repair-pdf"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
              <Download size={12}/> PDF
            </button>
          </div>
        </div>
      </div>

      {/* KPI hero */}
      {totals && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="repair-totals">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Grand Total Rehab</div>
              <div className="text-4xl font-bold text-slate-900 tabular-nums mt-1"
                   data-testid="repair-grand-total">
                ${fmt(totals.grand_total_rehab)}
              </div>
              <div className="text-xs text-slate-700 mt-1">
                Repairs <b>${fmt(totals.total_repair_cost)}</b>
                {" · "}Contingency <b>${fmt(totals.contingency_amount)}</b>
                {current?.contingency_pct != null && (
                  <> ({pct(current.contingency_pct)})</>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-right">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">$/sqft</div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums mt-1"
                     data-testid="repair-cost-per-sqft">
                  {totals.cost_per_sqft ? `$${fmt(totals.cost_per_sqft, 2)}` : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">% of ARV</div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums mt-1"
                     data-testid="repair-pct-of-arv">
                  {totals.repair_pct_of_arv ? pct(totals.repair_pct_of_arv) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Line items */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Line Items ({draft.items.length})
          </div>
          <button onClick={addItem} data-testid="repair-add-item"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900">
            <Plus size={12}/> Add item
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="repair-items-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Room</th>
                <th className="text-left px-3 py-2 font-medium">Category</th>
                <th className="text-left px-3 py-2 font-medium min-w-[200px]">Description</th>
                <th className="text-right px-3 py-2 font-medium">Qty</th>
                <th className="text-left px-3 py-2 font-medium">Unit</th>
                <th className="text-right px-3 py-2 font-medium">Unit $</th>
                <th className="text-right px-3 py-2 font-medium">Line $</th>
                <th className="px-2 py-2"/>
              </tr>
            </thead>
            <tbody>
              {draft.items.map((it, i) => {
                const qtyN = parseFloat(it.qty || 0);
                const costN = parseFloat(it.unit_cost || 0);
                const line = (Number.isNaN(qtyN) ? 0 : qtyN) * (Number.isNaN(costN) ? 0 : costN);
                return (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/40"
                      data-testid={`repair-row-${i}`}>
                    <td className="px-3 py-1.5">
                      <select value={it.room} onChange={(e) => updateItem(i, "room", e.target.value)}
                              className={INP_SM}>
                        {enums.rooms.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <select value={it.category} onChange={(e) => updateItem(i, "category", e.target.value)}
                              className={INP_SM}>
                        {enums.categories.map((c) => <option key={c} value={c}>{humanize(c)}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <input value={it.description}
                               onChange={(e) => updateItem(i, "description", e.target.value)}
                               className={INP_SM}
                               placeholder="e.g., Cabinet reface"
                               data-testid={`repair-desc-${i}`}/>
                        <select value=""
                                onChange={(e) => { if (e.target.value) applyTemplate(i, e.target.value); }}
                                className={`${INP_SM} max-w-[70px]`}
                                title="Apply template"
                                aria-label="Apply template">
                          <option value="">Tpl…</option>
                          {templates
                            .filter((t) => !it.category || t.category === it.category)
                            .map((t) => <option key={t.id} value={t.id}>{t.description}</option>)}
                        </select>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input type="number" value={it.qty}
                             onChange={(e) => updateItem(i, "qty", e.target.value)}
                             className={`${INP_SM} text-right`}
                             data-testid={`repair-qty-${i}`}/>
                    </td>
                    <td className="px-3 py-1.5">
                      <select value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)}
                              className={INP_SM}>
                        {enums.units.map((u) => <option key={u} value={u}>{humanize(u)}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input type="number" value={it.unit_cost}
                             onChange={(e) => updateItem(i, "unit_cost", e.target.value)}
                             className={`${INP_SM} text-right`}
                             data-testid={`repair-cost-${i}`}/>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-900 font-semibold"
                        data-testid={`repair-line-${i}`}>
                      ${fmt(line)}
                    </td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => removeItem(i)}
                              data-testid={`repair-remove-${i}`}
                              className="text-slate-400 hover:text-red-600" title="Remove">
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* By-category rollup */}
      {totals?.by_category && Object.keys(totals.by_category).length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="repair-donut-card">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Cost Distribution</div>
            <div className="text-sm font-semibold text-slate-900 mb-3">
              Where the rehab dollars flow
            </div>
            <RepairBreakdownDonut byCategory={totals.by_category} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="repair-by-category">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">By Category</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(totals.by_category)
              .sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]))
              .map(([cat, amount]) => {
                const total = parseFloat(totals.total_repair_cost || 0);
                const shareN = total > 0 ? (parseFloat(amount) / total) * 100 : 0;
                return (
                  <div key={cat}>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="text-slate-800 font-medium">{humanize(cat)}</span>
                      <span className="tabular-nums text-slate-600">
                        ${fmt(amount)} <span className="text-slate-400">· {shareN.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 mt-1 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full"
                           style={{ width: `${Math.min(100, shareN)}%` }}/>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        </div>
      )}

      {/* AI panel */}
      {current?.ai_recommendation && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4"
             data-testid="repair-ai-block">
          <div className="text-[10px] uppercase tracking-wider text-purple-700">AI Analysis</div>
          <div className="text-sm text-slate-900 mt-1">{current.ai_recommendation.summary}</div>
          {current.ai_recommendation.risk_level && (
            <div className="text-[11px] text-slate-700 mt-1">
              <b>Risk level:</b> {current.ai_recommendation.risk_level}
              {current.ai_recommendation.recommended_contingency_pct != null && (
                <> · <b>Recommended contingency:</b> {pct(current.ai_recommendation.recommended_contingency_pct)}</>
              )}
            </div>
          )}
          {current.ai_recommendation.missing_scope?.length > 0 && (
            <div className="text-[11px] text-slate-700 mt-1">
              <b>Missing scope:</b> {current.ai_recommendation.missing_scope.join("; ")}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {estimates.length > 1 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="repair-history">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            History ({estimates.length})
          </div>
          <div className="space-y-1">
            {estimates.map((e) => (
              <button key={e.id}
                      onClick={() => {
                        setCurrent(e);
                        setDraft({
                          name: e.name,
                          property_sqft: e.property_sqft || "",
                          property_arv: e.property_arv || "",
                          items: e.items?.length ? e.items : [EMPTY_ITEM()],
                        });
                      }}
                      data-testid={`repair-history-${e.id.slice(0, 8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>v{e.version} · {e.name} <span className="text-slate-400">· {e.status}</span></span>
                <span className="tabular-nums text-slate-600">
                  ${fmt(e.totals?.grand_total_rehab)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────── helpers

const ROOM_TO_CATEGORY = {
  roof: "roof", foundation: "foundation", exterior: "exterior",
  driveway: "landscaping", garage: "garage",
  kitchen: "kitchen", bathroom: "bathrooms", primary_bathroom: "bathrooms",
  laundry: "plumbing", mechanical: "hvac",
};

function humanize(s) {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmt(v, digits = 0) {
  if (v == null || v === "") return "0";
  const n = parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {
    maximumFractionDigits: digits, minimumFractionDigits: 0,
  });
}

function pct(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return `${(n * 100).toFixed(1)}%`;
}
